import * as Sentry from "@sentry/react";
import { supabase } from './lib/supabaseClient';
import React, { useState, useEffect, useContext, createContext, Suspense } from 'react';
import { BrowserRouter, Routes as ReactRouterRoutes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { CircularProgress, Box, Paper, Typography } from '@mui/material';
import { Clock, ShieldOff, AlertTriangle } from 'lucide-react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useProfileStore } from './store/profileStore';
import { usePushNotifications } from './hooks/usePushNotifications';
import DefaultLoader from "./components/common/defaultLoader";
import { useNativeStore } from './store/nativeStore';

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(ReactRouterRoutes);

const SentryErrorFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: '#fff0f0' }}>
    <Paper sx={{ p: 4, textAlign: 'center', maxWidth: '500px' }}>
      <AlertTriangle size={48} className="mx-auto text-red-600 mb-4" />
      <Typography variant="h5" component="h1" gutterBottom>
        An Error Occurred
      </Typography>
      <Typography variant="body1" color="text.secondary">
        We're sorry, but something went wrong. Our team has been automatically notified.
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        Please try refreshing the page.
      </Typography>
    </Paper>
  </Box>
);

export const ErrorButton = () => {
  return (
    <button
      type="button"
      className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
      onClick={() => {
        // Send a log before throwing the error
        Sentry.logger.info('User triggered test error', {
          action: 'test_error_button_click',
        });
        throw new Error('Sentry Test Error: This is a test error!');
      }}
    >
      Test Sentry Error
    </button>
  );
};

// Import components
const MainAppLayout = React.lazy(() => import('./components/layout/MainAppLayout'));
const Onboarding = React.lazy(() => import('./pages/Onboarding'));
const DoctorDashboard = React.lazy(() => import('./pages/DoctorDashboard'));
const VideoModulePage = React.lazy(() => import('./pages/VideoModule'));
const Database = React.lazy(() => import('./pages/Database'));
const PatientEMRPage = React.lazy(() => import('./pages/PatientEMRPage'));
const Feedback = React.lazy(() => import('./pages/FeedbackReviews'));
const Healthfeed = React.lazy(() => import('./pages/HealthFeed'));
const Help = React.lazy(() => import('./pages/Help'));
const Promotion = React.lazy(() => import('./pages/Promotions'));
const Notifications = React.lazy(() => import('./pages/Notification'));
const Settings = React.lazy(() => import('./pages/Settings'));
const Report = React.lazy(() => import('./pages/Report'));
const ProfileLayout = React.lazy(() => import('./pages/doctor-profile/ProfileLayout'));
const ApprovedProfileView = React.lazy(() => import('./pages/ApprovedProfileView'));
const ProfilePreview = React.lazy(() => import('./pages/ProfilePreview'));
const DoctorVerificationPage = React.lazy(() => import('./pages/DoctorVerificationPage'));
const PrescriptionPage = React.lazy(() => import('./components/prescription/PrescriptionPage'));
const BillingPage = React.lazy(() => import('./pages/Billing'));
const SubscriptionPage = React.lazy(() => import('./pages/Subscription'));
const WalletPage = React.lazy(() => import('./pages/WalletPage'));
const AppsHubPage = React.lazy(() => import('./pages/apps/AppsHubPage'));
const BreathyDesk = React.lazy(() => import('./pages/apps/breathy-desk/BreathyDesk'));
const OrganizationFormPage = React.lazy(() => import('./pages/apps/breathy-desk/OrganizationFormPage'));
const OrganizationDetailPage = React.lazy(() => import('./pages/apps/breathy-desk/OrganizationDetailPage'));
const FinancialsPage = React.lazy(() => import('./pages/apps/FinancialsPage'));
const InvoiceManager = React.lazy(() => import('./pages/apps/InvoiceManager'));
const CreateInvoicePage = React.lazy(() => import('./pages/apps/CreateInvoicePage'));
const InvoiceDetailPage = React.lazy(() => import('./pages/apps/InvoiceDetailPage'));
const InvoiceSettings = React.lazy(() => import('./pages/apps/InvoiceSettings'));
const ChatsPage = React.lazy(() => import('./pages/ChatsPage'));
const BlogManager = React.lazy(() => import('./pages/BlogManager'));
const ReferRewardPage = React.lazy(() => import('./pages/apps/ReferRewardPage'));
const FreehandReviewPage = React.lazy(() => import('./components/freehand/FreehandReviewPage'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {      
      refetchOnWindowFocus: false,
    },
  },
});
const theme = createTheme({
  palette: {
    primary: { main: '#14b8a6' },
    secondary: { main: '#38bdf8' },
  },
});

const FullPageLoader = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
    </Box>
);

const SuspendedProfilePage = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: '#fbe9e7' }}>
        <Paper sx={{ p: 4, textAlign: 'center', maxWidth: '500px' }}>
            <ShieldOff size={48} className="mx-auto text-red-600 mb-4" />
            <Typography variant="h5" component="h1" gutterBottom>Account Suspended</Typography>
            <Typography variant="body1" color="text.secondary">
                Your account has been suspended. Please contact support for further information.
            </Typography>
        </Paper>
    </Box>
);

const PendingApprovalPage = () => (
    
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Paper sx={{ p: 4, textAlign: 'center', maxWidth: '500px' }}>
                <Clock size={48} className="mx-auto text-amber-500 mb-4" />
                <Typography variant="h5" component="h1" gutterBottom>Profile Submitted for Review</Typography>
                <Typography variant="body1" color="text.secondary">
                    Thank you for submitting your profile. Our team will review it shortly. You will be notified once your profile is approved.
                </Typography>
            </Paper>
        </Box>
    
);



const AppFlow = () => {
    const [session, setSession] = useState(null);
    const [isLoadingSession, setIsLoadingSession] = useState(true);
    const fetchInitialStatus = useProfileStore(state => state.fetchInitialStatus);
    const user = session?.user;
    usePushNotifications(session?.user);

    useEffect(() => {
      if (user) {
        Sentry.setUser({
          id: user.id,
          email: user.email,
          // Add any other user data you want to track
        });
      } else {
        Sentry.setUser(null);
      }
    }, [user]);

    useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/firebase-messaging-sw.js') // Register the file
        .then((registration) => {

        })
        .catch((err) => {
          console.error('Service Worker registration failed:', err); // Check for errors here
        });
    }
  }, []);

      // --- NATIVE APP DETECTION ---
    const setIsNative = useNativeStore(state => state.setIsNative);

    useEffect(() => {
        // Option 1: Direct Global Variable injection from Native Webviews
        if (window.isNativeApp || window.ReactNativeWebView) {
            setIsNative(true);
        }

        // Option 2: Message listener for dynamic toggles
        const handleMessage = (event) => {
            // Check structured data
            if (event.data && typeof event.data === 'object') {
                if (event.data.type === 'SET_NATIVE_MODE') {
                    setIsNative(event.data.isNative ?? true);
                }
            } 
            // Check string payload
            else if (typeof event.data === 'string' && event.data === 'native_mode_enabled') {
                setIsNative(true);
            }
        };

        window.addEventListener('message', handleMessage);
        
        // Also fire a window event for other scripts to use
        window.addEventListener('native', () => setIsNative(true));
        
        return () => {
            window.removeEventListener('message', handleMessage);
            window.removeEventListener('native', () => setIsNative(true));
        };
    }, [setIsNative]);

    useEffect(() => {
        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
            
            setSession(session);
            if (session) {
                fetchInitialStatus();
            }
            setIsLoadingSession(false);
        });
        return () => subscription.unsubscribe();
    }, [fetchInitialStatus]); 
    
    if (isLoadingSession) {
        return <DefaultLoader />;
    }

    return (
        <SentryRoutes>
            <Route path="/login" element={!session ? <DoctorVerificationPage /> : <Navigate to="/" />} />
            <Route path="/*" element={session ? <AuthenticatedRoutes /> : <Navigate to="/login" />} />
        </SentryRoutes>
    );
};

const AuthenticatedRoutes = () => {
    const isLoadingStatus = useProfileStore((state) => state.isLoading);
    const profileStatusValue = useProfileStore((state) => state.profileStatus?.profile_status);

    // --- Sentry Context ---
    useEffect(() => {
      // This adds the profile_status to all Sentry events,
      // which is extremely useful for debugging
      Sentry.setTag("profile_status", profileStatusValue || "unknown");
    }, [profileStatusValue]);

    if (isLoadingStatus || !profileStatusValue) {
        return <DefaultLoader />;
    }

    switch (profileStatusValue) {
        case 'onboarding':
            
            return <Onboarding />;

        case 'in_progress':
            return (
                <SentryRoutes>
                    <Route element={<MainAppLayout />}>
                        <Route path="/" element={<DoctorDashboard />} />
                        <Route path="/profile" element={<ProfileLayout />} />
                        <Route path="/reports" element={<Report />} />
                        <Route path="/database" element={<Database />} />
                        <Route path="/healthfeed" element={<Healthfeed />} />
                        <Route path="/promotion" element={<Promotion />} />
                        <Route path="/feedback" element={<Feedback />} />
                        <Route path="/notifications" element={<Notifications />} />
                        <Route path="/settings" element={<Settings />} />
                        <Route path="/help" element={<Help />} />
                        <Route path="/billing" element={<BillingPage />} />
                        <Route path="/subscription" element={<SubscriptionPage />} />
                        <Route path="*" element={<Navigate to="/" />} />
                    </Route>
                </SentryRoutes>
            );
        case 'rejected':
            return (
                <SentryRoutes>
                    <Route path="/profile" element={<ProfileLayout />} />
                    <Route path="*" element={<Navigate to="/profile" />} />
                </SentryRoutes>
            );
        
        case 'suspended':
            return <SuspendedProfilePage />;

        case 'awaiting_review':
            
            return (
                <SentryRoutes>
                    <Route path="/video/:appointmentId" element={<VideoModulePage />} />
                    <Route path="/prescriptions" element={<PrescriptionPage />} />
                    <Route path="/prescriptions/review/:jobId" element={<FreehandReviewPage />} />
                    <Route path="/chats" element={<ChatsPage />} />
                        <Route path="/chats/:sessionId" element={<ChatsPage />} />
                    <Route element={<MainAppLayout />}>
                        <Route path="/" element={<DoctorDashboard />} />
                        <Route path="/profile" element={<PendingApprovalPage />} /> 
                        <Route path="/reports" element={<Report />} />
                        <Route path="/database" element={<Database />} />
                        
                        <Route path="/healthfeed" element={<Healthfeed />} />
                        <Route path="/promotion" element={<Promotion />} />
                        <Route path="/feedback" element={<Feedback />} />
                        <Route path="/notifications" element={<Notifications />} />
                        <Route path="/settings" element={<Settings />} />
                        <Route path="/help" element={<Help />} />
                        <Route path="/billing" element={<BillingPage />} />
                        <Route path="/subscription" element={<SubscriptionPage />} />
                        <Route path="*" element={<Navigate to="/" />} />
                    </Route>
                </SentryRoutes>
            );

        case 'approved':
           
            return (
                <SentryRoutes>
                    <Route path="/video/:appointmentId" element={<VideoModulePage />} />
                    <Route path="/prescriptions" element={<PrescriptionPage />} />
                    <Route path="/prescriptions/review/:jobId" element={<FreehandReviewPage />} />
                    <Route path="/chats" element={<ChatsPage />} />
                    <Route path="/chats/:sessionId" element={<ChatsPage />} />
                    

                    <Route element={<MainAppLayout />}>
                        <Route path="/" element={<DoctorDashboard />} />
                        <Route path="/profile" element={<ApprovedProfileView />} />
                         <Route path="/profile/preview" element={<ProfilePreview />} />
                        <Route path="/reports" element={<Report />} />
                        <Route path="/database" element={<Database />} />
                        
                        <Route path="/patient/:patientId" element={<PatientEMRPage />} />
                        <Route path="/healthfeed" element={<Healthfeed />} />
                        <Route path="/promotion" element={<Promotion />} />
                        <Route path="/feedback" element={<Feedback />} />
                        <Route path="/notifications" element={<Notifications />} />
                        <Route path="/blog-manager" element={<BlogManager />} />
                        <Route path="/settings" element={<Settings />} />
                        <Route path="/help" element={<Help />} />
                        <Route path="/billing" element={<BillingPage />} />
                        <Route path="/apps" element={<AppsHubPage />} />
                        <Route path="/apps/breathy-desk" element={<BreathyDesk />} />
                        <Route path="/apps/breathy-desk/new" element={<OrganizationFormPage />} />
                        <Route path="/apps/breathy-desk/:organizationId" element={<OrganizationDetailPage />} />
                        <Route path="/apps/financialsPage" element={<FinancialsPage />} />
                        <Route path="/apps/billing" element={<InvoiceManager />} />
                        <Route path="/apps/billing/settings" element={<InvoiceSettings />} />
                        <Route path="/apps/billing/create" element={<CreateInvoicePage />} />
                        <Route path="/apps/billing/view/:invoiceId" element={<InvoiceDetailPage />} />
                        <Route path="/subscription" element={<SubscriptionPage />} />
                        <Route path="/wallet" element={<WalletPage />} />
                        <Route path="/apps/refer-reward" element={<ReferRewardPage />} />
                        <Route path="*" element={<Navigate to="/" />} />
                    </Route>
                </SentryRoutes>
            );

        default: return <DefaultLoader />;
    }
};

export default function AppWrapper() {
    return (
      <Sentry.ErrorBoundary fallback={<SentryErrorFallback />}>
        <BrowserRouter>
            <QueryClientProvider client={queryClient}>
                <ThemeProvider theme={theme}>
                    <CssBaseline />
                    
                        <Suspense fallback={<DefaultLoader />}>
                            <AppFlow />
                        </Suspense>
                    
                    <ToastContainer position="bottom-right" autoClose={5000} hideProgressBar={false} />
                </ThemeProvider>
            </QueryClientProvider>
        </BrowserRouter>
      </Sentry.ErrorBoundary>
    );
}
